import { AutocompleteProps, Box, ListItem, Typography } from '@mui/material';
import Highlighter from 'react-highlight-words';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { isEmptyWhereClause } from '../utils';
import { useFetchWisdomPrompts } from './useFetchWisdomPrompts';
import { useIsDynamicPromptsEnabled } from './useIsDynamicPromptsEnabled';

interface UseWisdomPromptAutoCompleteOptions {
  queryContainer: QueryContainer;
}

export const useWisdomPromptAutoCompleteOptions = ({
  queryContainer,
}: UseWisdomPromptAutoCompleteOptions):
  | Partial<AutocompleteProps<string, false, true, true>>
  | undefined => {
  const isDynamicPromptsEnabled = useIsDynamicPromptsEnabled();

  const {
    prompts: wisdomPromptSuggestions,
    loading: areLoadingPromptSuggestions,
    error: promptSuggestionsError,
  } = useFetchWisdomPrompts({
    queryContainer,
    skip:
      !isDynamicPromptsEnabled ||
      isEmptyWhereClause(queryContainer.WhereClause),
  });

  const renderWisdomOptions = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
  ) => {
    const boldedTexts = wisdomPromptSuggestions?.find(
      ({ prompt }) => prompt === option,
    )?.boldedTexts;

    return (
      <ListItem {...props}>
        <Typography variant="body2" py={0.5}>
          <Highlighter
            searchWords={boldedTexts ?? []}
            highlightTag={({ children }) => (
              <Box component="strong">{children}</Box>
            )}
            textToHighlight={option}
          />
        </Typography>
      </ListItem>
    );
  };

  if (areLoadingPromptSuggestions) {
    return {
      loading: true,
      options: [],
    };
  }

  if (wisdomPromptSuggestions?.length && !promptSuggestionsError) {
    return {
      loading: areLoadingPromptSuggestions,
      options: wisdomPromptSuggestions.map(({ prompt }) => prompt),
      renderOption: renderWisdomOptions,
    };
  }
};
